import { unlinkedCompanyProfileNotice } from '@/lib/companyProfileNotice';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import {
  v2,
  ApiError,
  type CompiledPackageSummary,
  type CompileExcludedDocument,
  type SubmissionValidationResultsResponse,
  type CompanyProfileLinkStatus,
  parseApiErrorBody,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { describeApiError } from '@/lib/apiErrorView';
import { classifyCompile422Text, type Compile422 } from '@/lib/compiler/compileBlock';
import { CompileBlockNotice, type CompileBlockTestIds } from './CompileBlockNotice';

/** T-17 (T17-D9) — `submit-blocked-incomplete` keeps its pre-existing id. */
const PANEL_BLOCK_TEST_IDS: CompileBlockTestIds = {
  incomplete: 'submit-blocked-incomplete',
  'checklist-auto': 'compile-block-checklist',
  'no-confirmed': 'compile-block-no-confirmed',
  'slot-format': 'compile-block-slot-format',
};
import {
  initialFlowState,
  nextStep,
  applyChecklistComplete,
  applySignatureCaptured,
  applyPublishMode,
  applyUnplacedFormsAck,
  applySeqOrderAck,
  applyCancel,
  requiredAckIdsOf,
  type ChecklistItem,
  type PublishGovernanceFlowState,
  type PublishGovernanceSettings,
  type SubmissionPublishContext,
  type SignatureAttestation,
} from '@/lib/compiler/publishGovernanceFlow';
import {
  PrePublishChecklistModal,
  ESignatureReauthModal,
  PublishIntentModal,
  UnplacedFormsWarningModal,
  SequenceOrderWarningModal,
} from './publish-governance';

// Wave E.7 — Compile button + results UI.
//
// Lives below the ValidationStatusBanner on /compiler. Renders:
//
//   - A primary "Compile" button (disabled unless Submissions:write —
//     T17-D9: the server's POST /compile gate; was mis-documented as Documents:write).
//     Label shows the doc count + error/warning counts pulled from the
//     last validation pass so the operator knows what they're getting into.
//   - On click: confirmation modal. If validation errors exist, the modal
//     requires an override reason in a textarea before submitting.
//   - On success: a CompiledPackageDownload sub-panel renders with three
//     download options (zip / folder via File System Access API / fallback).
//   - On 422 gate-block: re-opens the modal with the specific errors and
//     forces the override-reason path.
//
// Three download paths from Q17 of WAVE_E_DESIGN_SPEC_v3:
//   1. Download as .zip                 — anchor + signed-URL redirect.
//   2. Write to folder (Chrome/Edge)    — File System Access API path.
//   3. Folder structure download (fallback) — same as zip for now; the
//      File System Access API path will gracefully fall back to .zip
//      if the browser doesn't support it.

interface CompileSubmissionPanelProps {
  /** The submission being compiled. Required — panel hides when null. */
  readonly submissionId: string | null;
  /** Whether the current user can trigger compile. */
  readonly canCompile: boolean;
  /** Latest validation snapshot for the count labels on the button. */
  readonly validation: SubmissionValidationResultsResponse | null;
  /**
   * Phase 5.X-publish-governance — the publish gates for THIS submission
   * (`preP` = checklist, `reAuth` = e-signature). Default
   * `{preP: false, reAuth: false}` (no extra modals).
   *
   * T-17 (T17-D2) — the caller must pass the backend-resolved governance
   * (own company profile -> org setting -> false), i.e. the `governance`
   * block of GET /submissions/:id/prepublish-checklist
   * (`usePublishGovernance`). The old comment here said the parent reads it
   * from `v2.settings.getSubmission`, which was never the source (the
   * submission's own profile is). This modal chain is DORMANT in the app —
   * CompilerPage always supplies `onPublishStart`, so the PublishWizard (which
   * fetches governance itself and fails closed) runs instead; the default of
   * "off" here only matters to a caller that omits both.
   */
  readonly publishGovernanceSettings?: PublishGovernanceSettings;
  /**
   * Phase 5.X-publish-governance — submission context for the warning
   * modals. Default `{unplacedFormsCount: 0, outOfSequenceOrder: false}`
   * (warnings skipped). Parent computes from validation results /
   * submission metadata when those signals are wired up.
   */
  readonly publishContext?: SubmissionPublishContext;
  /**
   * Phase 5.X-publish-governance — called when the seq-order modal's
   * "Reorder first" is clicked. Optional; when absent the modal shows
   * "Cancel — reorder manually first" instead.
   */
  readonly onReorderFirst?: () => void;
  /**
   * Phase 5.X-publish-governance — called when the unplaced-forms
   * modal's "Place now" is clicked. Optional; when absent, the button
   * is still shown but does nothing other than cancel the flow (parent
   * navigates manually).
   */
  readonly onPlaceUnplacedForms?: () => void;
  /**
   * Submission sequence number (e.g. "0003"). Passed to
   * SequenceOrderWarningModal so it can display the human-readable
   * sequence rather than the submission UUID. Optional; falls back to
   * "(current)" when absent.
   */
  readonly submissionSequenceNumber?: string | null;
  /**
   * Phase phase-publish-as-mode (TODO-022) — when provided, clicking
   * the "Compile" button INVOKES THIS CALLBACK INSTEAD OF opening the
   * governance-modal chain. The parent (CompilerPage) uses it to flip
   * a `publishMode` flag that swaps the assembly UI for an in-place
   * PublishWizard. When omitted, the legacy modal chain runs.
   */
  readonly onPublishStart?: () => void;
  /**
   * phase-missing-profile-visibility (2026-08-03) then
   * phase-report-followups-batch item 1 (Sharon's ruling, 2026-08-04) —
   * the applicant identity written into the compiled regional XML is one
   * of the generation paths a broken company-profile link affects. Compile
   * now REFUSES (409, via submissionLoader.ts → the shared
   * resolveCompanyProfileForSubmission evaluator) exactly like the
   * Admin Forms / cover-letter paths, so the button is disabled here too —
   * consistent with their gating. An UNLINKED submission (no profile ever
   * chosen) still compiles via the fallback profile, unchanged — that
   * behaviour is a separate, still-open decision — but the fallback
   * profile used is named on the result once compiled.
   */
  readonly companyProfileLinkStatus?: CompanyProfileLinkStatus;
}

const DEFAULT_GOV_SETTINGS: PublishGovernanceSettings = { preP: false, reAuth: false };

/** The required ack ids are the SERVED manual items' (T17-D14); unknown until fetched. */
function withRequiredAckIds(
  settings: PublishGovernanceSettings,
  items: ReadonlyArray<ChecklistItem> | null,
): PublishGovernanceSettings {
  return items === null ? settings : { ...settings, requiredAckIds: requiredAckIdsOf(items) };
}
const DEFAULT_CONTEXT: SubmissionPublishContext = {
  unplacedFormsCount: 0,
  outOfSequenceOrder: false,
};

const panelStyle: CSSProperties = {
  marginBottom: 16,
  padding: 12,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const buttonRowStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
};

const primaryButtonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: 'var(--bg)',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: CSSProperties = {
  background: 'none',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 12,
  cursor: 'pointer',
};



const textareaStyle: CSSProperties = {
  width: '100%',
  minHeight: 80,
  padding: 8,
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontFamily: 'inherit',
  fontSize: 13,
  boxSizing: 'border-box',
};

export function CompileSubmissionPanel({
  submissionId,
  canCompile,
  validation,
  publishGovernanceSettings = DEFAULT_GOV_SETTINGS,
  publishContext = DEFAULT_CONTEXT,
  onReorderFirst,
  onPlaceUnplacedForms,
  submissionSequenceNumber = null,
  onPublishStart,
  companyProfileLinkStatus,
}: CompileSubmissionPanelProps): ReactElement | null {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  // T-17 (T17-D9) — ONE state for a refused compile (the shared classifier in
  // `@/lib/compiler/compileBlock`). `gate` reopens the override flow in the
  // confirm dialog; every other kind renders as a notice; `unknown` is `error`.
  const [compileBlock, setCompileBlock] = useState<Exclude<Compile422, { kind: 'unknown' }> | null>(
    null,
  );
  const gateErrors = compileBlock?.kind === 'gate' ? compileBlock : null;
  const [error, setError] = useState<string | null>(null);
  const [pkg, setPkg] = useState<CompiledPackageSummary | null>(null);
  // phase-partial-compile-with-exclusions — the exclusions of the last
  // compile (empty when COMPLETE) drive the honest partial-compile warning;
  // `compileBlock.kind === 'incomplete'` carries the RED-LINE refusal when a
  // FINAL publish is attempted on an incomplete set.
  const [compileExcluded, setCompileExcluded] = useState<CompileExcludedDocument[]>([]);
  // Phase 5.X-publish-governance — flow state. `flow.step` drives which
  // modal renders; `nextStep()` advances on each transition.
  const [flow, setFlow] = useState<PublishGovernanceFlowState>(initialFlowState);
  // Phase 5.X-prepublish-checklist-autodetect — auto-detected
  // checklist items fetched when the checklist step opens. `null`
  // until the first fetch completes; the modal renders only the
  // manual items in the meantime (auto items appear as soon as the
  // GET resolves).
  // T-17 (T17-D14): now ALL the served items (auto + manual) — the web keeps no
  // list of its own. `null` until the fetch resolved.
  const [checklistItems, setChecklistItems] = useState<ReadonlyArray<ChecklistItem> | null>(
    null,
  );
  const advance = useCallback(
    (updater: (s: PublishGovernanceFlowState) => PublishGovernanceFlowState) => {
      setFlow((prev) => {
        const next = updater(prev);
        const step = nextStep(
          next,
          withRequiredAckIds(publishGovernanceSettings, checklistItems),
          publishContext,
        );
        return { ...next, step };
      });
    },
    [publishGovernanceSettings, publishContext, checklistItems],
  );
  // When flow.step lands on 'compile', auto-trigger the existing
  // confirm modal (which handles validation-gate / override-reason).
  useEffect(() => {
    if (flow.step === 'compile') {
      setOverrideReason('');
      setError(null);
      setCompileBlock(null);
      setConfirmOpen(true);
    }
  }, [flow.step]);

  // Phase 5.X-prepublish-checklist-autodetect — fetch auto items when
  // the checklist step opens. Re-fetch every time the checklist is
  // shown so a re-opened modal reflects fresh DB state (e.g., the
  // operator confirmed a doc in another tab between attempts).
  useEffect(() => {
    if (flow.step !== 'checklist' || !submissionId) return;
    let cancelled = false;
    setChecklistItems(null);
    void v2.submissions
      .getPrePublishChecklist(submissionId)
      .then((res) => {
        if (cancelled) return;
        setChecklistItems(res.items);
      })
      .catch((err: unknown) => {
        // T-17 (T17-D14) — FAIL CLOSED. This used to fall back to an empty
        // auto list with the web's own built-in manual items, so a failed fetch
        // quietly ran the checklist against a list the server never served. The
        // items are the server's: without them the chain stops, with the reason.
        if (cancelled) return;
        setError(
          `Could not load the pre-publish checklist — publishing is blocked until it loads. ${formatApiError(err, 'Please retry.')}`,
        );
        setConfirmOpen(false);
        setFlow(initialFlowState());
      });
    return () => {
      cancelled = true;
    };
  }, [flow.step, submissionId]);

  // T-17 (T17-D3): "Run validation now" for a never-validated submission, then
  // re-serve the checklist so the verdict replaces the unknown row.
  const [validationRunning, setValidationRunning] = useState(false);
  const [validationRunError, setValidationRunError] = useState<string | null>(null);
  const runChecklistValidation = useCallback(async (): Promise<void> => {
    if (!submissionId) return;
    setValidationRunning(true);
    setValidationRunError(null);
    try {
      await v2.submissions.validateNow(submissionId);
      const res = await v2.submissions.getPrePublishChecklist(submissionId);
      setChecklistItems(res.items);
    } catch (err: unknown) {
      setValidationRunError(formatApiError(err, 'Validation could not be run. Try again.'));
    } finally {
      setValidationRunning(false);
    }
  }, [submissionId]);

  const docCount = validation?.documentCount ?? null;
  const errorCount = validation?.errorCount ?? 0;
  const warningCount = validation?.warningCount ?? 0;

  const buttonLabel = (() => {
    const parts: string[] = ['Compile'];
    const ctx: string[] = [];
    if (docCount !== null) ctx.push(`${docCount} doc${docCount === 1 ? '' : 's'}`);
    if (errorCount > 0) ctx.push(`${errorCount} error${errorCount === 1 ? '' : 's'}`);
    else if (warningCount > 0) ctx.push(`${warningCount} warning${warningCount === 1 ? '' : 's'}`);
    if (ctx.length > 0) parts.push(`(${ctx.join(', ')})`);
    return parts.join(' ');
  })();

  // Compile click. With Phase phase-publish-as-mode (TODO-022), when
  // `onPublishStart` is wired by the parent (CompilerPage), the click
  // hands control to the in-place PublishWizard instead of opening the
  // legacy modal chain. The legacy chain remains as a fallback for
  // call sites that haven't migrated yet.
  const openConfirm = useCallback(() => {
    if (onPublishStart) {
      onPublishStart();
      return;
    }
    setFlow(() => {
      const fresh = initialFlowState();
      const step = nextStep(fresh, publishGovernanceSettings, publishContext);
      return { ...fresh, step };
    });
  }, [onPublishStart, publishGovernanceSettings, publishContext]);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
    // Reset the flow so cancelling from the confirm modal returns
    // the operator to the idle button (re-opening Compile starts fresh).
    setFlow(initialFlowState());
  }, []);

  const cancelFlow = useCallback(() => {
    setFlow((prev) => applyCancel(prev));
    setConfirmOpen(false);
    globalThis.setTimeout(() => setFlow(initialFlowState()), 0);
  }, []);

  const submitCompile = useCallback(async (): Promise<void> => {
    if (!submissionId) return;
    if (flow.publishMode === null) {
      setError('Internal: publishMode missing from flow state');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Phase 5.X-publish-governance — build the full body including
      // the governance attestation + checklist + warning acks.
      const body: Parameters<typeof v2.submissions.compile>[1] = {
        publishMode: flow.publishMode,
        unplacedFormsAck: flow.unplacedFormsAck,
        seqOrderAck: flow.seqOrderAck,
      };
      if (overrideReason.trim()) body.overrideReason = overrideReason.trim();
      if (flow.signatureAttestation) {
        body.signatureAttestation = {
          signerOid: flow.signatureAttestation.signerOid,
          signerEmail: flow.signatureAttestation.signerEmail,
          signatureTimestampUtc: flow.signatureAttestation.signatureTimestampUtc,
          signatureMeaning: flow.signatureAttestation.signatureMeaning,
          attestationToken: flow.signatureAttestation.attestationToken,
        };
      }
      if (Object.keys(flow.checklistAcknowledgments).length > 0) {
        body.checklistAcknowledgments = { ...flow.checklistAcknowledgments };
      }
      const res = await v2.submissions.compile(submissionId, body);
      setPkg(res.package);
      // phase-partial-compile-with-exclusions — record the exclusions so the
      // partial-compile warning names them (empty ⇒ COMPLETE, no warning).
      setCompileExcluded(res.excluded ?? []);
      setConfirmOpen(false);
      setCompileBlock(null);
      setFlow(initialFlowState());
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        // T-17 (T17-D9) — classified by the shared module; no kind is assumed.
        const block = classifyCompile422Text(err.body);
        if (block.kind === 'unknown') {
          setCompileBlock(null);
          setConfirmOpen(false);
          setError(block.message ?? 'Compile was blocked, but the server response could not be read.');
        } else {
          setCompileBlock(block);
          // Only the validation gate stays in the dialog (override flow); every
          // other block needs the operator to act elsewhere first.
          if (block.kind !== 'gate') setConfirmOpen(false);
        }
      } else if (err instanceof ApiError) {
        // phase-fix-publish-wizard-mode — same honest-error idiom as the
        // wizard: an ApiError's `message` IS the raw response body; render the
        // server's human message instead, never the raw JSON. (This modal-chain
        // path is dormant while CompilerPage supplies onPublishStart, but it
        // must not carry the defect.)
        const parsed = parseApiErrorBody(err);
        setError(parsed?.message ?? `Compile failed (HTTP ${err.status}).`);
      } else {
        setError(err instanceof Error ? err.message : 'Compile failed');
      }
    } finally {
      setBusy(false);
    }
  }, [submissionId, overrideReason, flow]);

  if (!submissionId) return null;

  const gateBlocking = gateErrors !== null;
  const overrideRequired = gateBlocking;

  // phase-report-followups-batch (item 1) — compile now refuses on a
  // broken link, so the button is disabled exactly like Admin Forms'
  // per-form Generate button, with the same reason shown.
  const profileMissing = companyProfileLinkStatus?.state === 'missing';
  const profileNotice = profileMissing
    ? 'Company profile missing — Compile will refuse. Restore the profile or relink the submission before compiling.'
    : companyProfileLinkStatus?.state === 'unlinked'
      ? unlinkedCompanyProfileNotice(companyProfileLinkStatus.profileCount)
      : null;

  return (
    <div data-testid="compile-panel" style={panelStyle}>
      {profileNotice && (
        <div
          data-testid="compile-profile-notice"
          role="status"
          style={{
            color:
              companyProfileLinkStatus?.state === 'missing'
                ? 'var(--danger, #b91c1c)'
                : 'var(--amber, #b45309)',
            fontSize: 12,
            padding: '6px 10px',
            borderRadius: 4,
            background:
              companyProfileLinkStatus?.state === 'missing'
                ? 'var(--danger-bg, rgba(185,28,28,0.08))'
                : 'rgba(180,83,9,0.08)',
          }}
        >
          ⚠ {profileNotice}
        </div>
      )}
      <div style={buttonRowStyle}>
        <button
          type="button"
          data-testid="compile-button"
          disabled={!canCompile || busy || profileMissing}
          onClick={openConfirm}
          title={
            profileMissing
              ? 'Company profile missing; Compile is unavailable until it is restored or relinked.'
              : undefined
          }
          style={{
            ...primaryButtonStyle,
            cursor: !canCompile || busy || profileMissing ? 'not-allowed' : 'pointer',
            opacity: !canCompile || busy || profileMissing ? 0.6 : 1,
          }}
        >
          {busy ? 'Compiling…' : buttonLabel}
        </button>
        {!canCompile && (
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
            Compiling needs the Submissions:write permission.
          </span>
        )}
      </div>
      {error && (
        <div role="alert" data-testid="compile-error" style={{ color: 'var(--danger, #b91c1c)', fontSize: 12 }}>
          {error}
        </div>
      )}
      {/* T-17 (T17-D9) — every non-overridable compile 422 (incomplete compile =
          the RED LINE, checklist auto-gate, no confirmed documents, slot-format)
          renders through the one shared notice. The old PUBLISH_BLOCKED_UNCONFIRMED
          panel is gone: the server has no emitter for it. A ValidationGateBlock is
          not shown here — it reopens the confirm dialog with the override flow. */}
      {compileBlock && compileBlock.kind !== 'gate' && (
        <div style={{ marginTop: 12 }}>
          <CompileBlockNotice
            block={compileBlock}
            testIds={PANEL_BLOCK_TEST_IDS}
            onDismiss={() => setCompileBlock(null)}
            dismissTestId="compile-block-dismiss"
          />
        </div>
      )}
      {/* phase-partial-compile-with-exclusions — PARTIAL compile succeeded:
          the package is a DRAFT and named documents were excluded. A WARNING
          (not an error) — the compile is legitimate for iteration, it just
          isn't submittable. */}
      {pkg && compileExcluded.length > 0 && (
        <div
          role="status"
          data-testid="partial-compile-warning"
          style={{
            marginTop: 8,
            padding: 12,
            border: '1px solid #FDBA74',
            borderRadius: 6,
            background: '#FFF7ED',
            color: '#9A3412',
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            ⚠️ Draft package — compiled {pkg.totalLeafFiles} document
            {pkg.totalLeafFiles === 1 ? '' : 's'}, {compileExcluded.length} excluded
          </div>
          <div style={{ marginBottom: 8 }}>
            This is a DRAFT for review. Confirm the excluded document
            {compileExcluded.length === 1 ? '' : 's'} and recompile for a complete,
            submittable package.
          </div>
          <ul
            data-testid="partial-compile-exclusions"
            style={{ margin: '4px 0 0 16px', padding: 0, listStyle: 'disc' }}
          >
            {compileExcluded.map((d) => (
              <li key={d.documentId} style={{ marginBottom: 2 }}>
                {d.fileName}
                {d.module && <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.8 }}>({d.module})</span>}
                <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.8 }}>— {d.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {pkg && <CompiledPackageDownload submissionId={submissionId} pkg={pkg} partial={compileExcluded.length > 0} />}

      {/* Phase 5.X-publish-governance — governance modal stack. Only one
          renders at a time; nextStep() in the orchestrator decides
          which. The compile step doesn't render a governance modal —
          it auto-opens the existing confirm modal via the useEffect
          above. */}
      {flow.step === 'checklist' && checklistItems !== null && (
        <PrePublishChecklistModal
          items={checklistItems}
          onRunValidation={() => void runChecklistValidation()}
          validationRunning={validationRunning}
          validationRunError={validationRunError}
          onContinue={(acks) => advance((s) => applyChecklistComplete(s, acks))}
          onCancel={cancelFlow}
          requiresReauth={publishGovernanceSettings.reAuth}
        />
      )}
      {flow.step === 'esig' && (
        <ESignatureReauthModal
          onSignatureCaptured={(attestation: SignatureAttestation) =>
            advance((s) => applySignatureCaptured(s, attestation))
          }
          onCancel={cancelFlow}
        />
      )}
      {flow.step === 'intent' && (
        <PublishIntentModal
          onContinue={(mode) => advance((s) => applyPublishMode(s, mode))}
          onCancel={cancelFlow}
        />
      )}
      {flow.step === 'unplaced' && (
        <UnplacedFormsWarningModal
          unplacedFormsCount={publishContext.unplacedFormsCount}
          onPublishAnyway={() => advance((s) => applyUnplacedFormsAck(s))}
          onPlaceNow={() => {
            cancelFlow();
            onPlaceUnplacedForms?.();
          }}
          onCancel={cancelFlow}
        />
      )}
      {flow.step === 'seqorder' && (
        <SequenceOrderWarningModal
          currentSequence={submissionSequenceNumber ?? '(current)'}
          lowestUnlockedSequence={null}
          reorderAvailable={Boolean(onReorderFirst)}
          onPublishAnyway={() => advance((s) => applySeqOrderAck(s))}
          onReorderFirst={() => {
            cancelFlow();
            onReorderFirst?.();
          }}
          onCancel={cancelFlow}
        />
      )}

      {confirmOpen && (
        /* phase-movable-dialogs — shared movable shell. */
        <MovableDialog
          persistKey="compiler.compileConfirm"
          title="Compile submission for FDA ESG?"
          testId="compile-confirm"
          onClose={busy ? undefined : closeConfirm}
          escDisabled={busy}
          width="min(540px, 90vw)"
          maxHeight="80vh"
          zIndex={1000}
          bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
            <p style={{ margin: 0, fontSize: 13 }}>
              {`This will rename every file per ICH M8, compute MD5 checksums, generate the eCTD XML backbone, and assemble the package in blob storage.`}
            </p>
            {gateBlocking && gateErrors && (
              <div
                data-testid="compile-gate-errors"
                style={{
                  border: '1px solid var(--danger, #b91c1c)',
                  borderRadius: 4,
                  padding: 8,
                  background: 'rgba(185,28,28,0.05)',
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {gateErrors.errors.length} validation error
                  {gateErrors.errors.length === 1 ? '' : 's'} block compile:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {gateErrors.errors.slice(0, 5).map((e) => (
                    <li key={e.resultId}>
                      <code style={{ fontSize: 11 }}>{e.ruleName}</code>{' '}
                      ({e.targetType}): {e.message ?? '(no message)'}
                    </li>
                  ))}
                  {gateErrors.errors.length > 5 && (
                    <li>…and {gateErrors.errors.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12 }}>
              Override reason{overrideRequired ? ' (required)' : ' (optional)'}:
              <textarea
                data-testid="compile-override-reason"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                style={textareaStyle}
                placeholder={
                  overrideRequired
                    ? 'Explain why these errors are acceptable. Recorded in the audit log.'
                    : 'Leave blank to require validation to pass cleanly.'
                }
              />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="compile-cancel"
                onClick={closeConfirm}
                style={secondaryButtonStyle}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="compile-confirm-submit"
                onClick={() => void submitCompile()}
                disabled={busy || (overrideRequired && overrideReason.trim().length === 0)}
                style={{
                  ...primaryButtonStyle,
                  opacity:
                    busy || (overrideRequired && overrideReason.trim().length === 0)
                      ? 0.6
                      : 1,
                }}
              >
                {busy
                  ? 'Compiling…'
                  : gateBlocking
                    ? 'Override + compile'
                    : 'Compile'}
              </button>
            </div>
        </MovableDialog>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  CompiledPackageDownload — post-compile chooser.
// ────────────────────────────────────────────────────────────────────────

interface CompiledPackageDownloadProps {
  readonly submissionId: string;
  readonly pkg: CompiledPackageSummary;
  /** phase-partial-compile-with-exclusions — label the export as a partial
   *  (draft) package. The eCTD bytes are never modified; the label is UI-only. */
  readonly partial?: boolean;
}

function CompiledPackageDownload({
  submissionId,
  pkg,
  partial = false,
}: CompiledPackageDownloadProps): ReactElement {
  const [busy, setBusy] = useState(false);
  const [writeStatus, setWriteStatus] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // T17-D8 — authenticated zip download (a bare <a href> carried no auth header).
  const [zipBusy, setZipBusy] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);

  const downloadZip = useCallback(async (): Promise<void> => {
    setZipBusy(true);
    setZipError(null);
    try {
      await v2.submissions.compileDownload(submissionId, pkg.id);
    } catch (err) {
      setZipError(describeApiError(err, 'The package could not be downloaded.').message);
    } finally {
      setZipBusy(false);
    }
  }, [submissionId, pkg.id]);

  // The File System Access API is Chrome/Edge only. The presence check
  // is the canonical feature-detection pattern.
  const fsApiSupported =
    typeof window !== 'undefined' && 'showDirectoryPicker' in window;

  const writeToFolder = useCallback(async (): Promise<void> => {
    setBusy(true);
    setWriteStatus('Picking destination folder…');
    setWriteError(null);
    try {
      // Type-cast through `unknown` because the File System Access API
      // isn't in the TS lib yet (it's still Stage 3).
      const win = window as unknown as {
        showDirectoryPicker: () => Promise<FileSystemDirectoryHandle>;
      };
      const dirHandle = await win.showDirectoryPicker();
      setWriteStatus('Fetching folder manifest…');
      const manifest = await v2.submissions.getCompileFolderManifest(submissionId, pkg.id);
      setWriteStatus(`Writing ${manifest.fileCount} file${manifest.fileCount === 1 ? '' : 's'}…`);
      let written = 0;
      for (const file of manifest.files) {
        // T17-D7/D8 — local-style storage has no signed url: those files are
        // fetched through the authenticated per-file route. A cloud entry
        // carries a signed url that must NOT receive our Authorization header.
        const res = file.requiresAuth
          ? await v2.submissions.fetchCompileFile(submissionId, pkg.id, file.relativePath)
          : file.url
            ? await fetch(file.url)
            : null;
        if (res === null) throw new Error(`No download link for ${file.relativePath}`);
        if (!res.ok) throw new Error(`Failed to fetch ${file.relativePath}: ${res.status}`);
        const buf = await res.arrayBuffer();
        await writeFileToDir(dirHandle, file.relativePath, buf);
        written += 1;
        setWriteStatus(`Wrote ${written} / ${manifest.fileCount} file${manifest.fileCount === 1 ? '' : 's'}…`);
      }
      setWriteStatus(`Done — wrote ${written} files to your chosen folder.`);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setWriteStatus(null); // user cancelled — no error
      } else {
        setWriteError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }, [submissionId, pkg.id]);

  return (
    <div
      data-testid="compiled-package-download"
      style={{
        marginTop: 8,
        padding: 12,
        background: 'var(--surface-alt, var(--surface))',
        borderRadius: 6,
        border: '1px solid var(--accent)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontWeight: 600 }}>
        {partial ? 'Partial package compiled (draft)' : 'Package compiled ✓'} —{' '}
        {pkg.totalLeafFiles} leaf
        {pkg.totalLeafFiles === 1 ? '' : 's'},{' '}
        {pkg.totalSizeBytes ? formatBytes(pkg.totalSizeBytes) : 'unknown size'}
      </div>
      {partial && (
        <div
          data-testid="export-partial-label"
          style={{ fontSize: 11, color: '#9A3412', fontWeight: 600 }}
        >
          ⚠️ This export is a PARTIAL package — for review only, not for submission.
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        Validation: <strong>{pkg.validationStatus}</strong>
        {/* phase-report-followups-batch (item 1) — the fallback (unlinked)
            case keeps compiling unchanged, but the result must state which
            profile was used. Shown for every compile, not only the
            fallback case, so the applicant identity is never a guess. */}
        {pkg.appliedCompanyProfileName && (
          <>
            {' '}
            · Applicant identity used:{' '}
            <strong data-testid="compile-applied-profile">
              {pkg.appliedCompanyProfileName}
            </strong>
          </>
        )}
        {pkg.overallSha256 && (
          <>
            {' '}
            · zip SHA-256:{' '}
            <code style={{ fontFamily: 'monospace' }}>
              {pkg.overallSha256.slice(0, 12)}…
            </code>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {pkg.hasZip && (
          <button
            type="button"
            data-testid="download-zip"
            disabled={zipBusy}
            onClick={() => void downloadZip()}
            style={{
              ...primaryButtonStyle,
              cursor: zipBusy ? 'not-allowed' : 'pointer',
              opacity: zipBusy ? 0.6 : 1,
            }}
          >
            {zipBusy ? 'Preparing download…' : 'Download as .zip'}
          </button>
        )}
        {fsApiSupported ? (
          <button
            type="button"
            data-testid="write-to-folder"
            disabled={busy}
            onClick={() => void writeToFolder()}
            style={{
              ...secondaryButtonStyle,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Write to a folder I'll pick
          </button>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
            (Direct folder write requires Chrome or Edge — use the .zip option.)
          </span>
        )}
      </div>
      {zipError && (
        <div data-testid="download-zip-error" role="alert" style={{ color: 'var(--danger, #b91c1c)', fontSize: 12 }}>
          {zipError}
        </div>
      )}
      {writeStatus && (
        <div data-testid="write-status" style={{ fontSize: 12 }}>
          {writeStatus}
        </div>
      )}
      {writeError && (
        <div data-testid="write-error" role="alert" style={{ color: 'var(--danger, #b91c1c)', fontSize: 12 }}>
          {writeError}
        </div>
      )}
    </div>
  );
}

/** Walk a relative path (`a/b/c.pdf`), creating each intermediate dir
 *  handle as needed, then write the bytes at the leaf filename. */
async function writeFileToDir(
  rootHandle: FileSystemDirectoryHandle,
  relativePath: string,
  data: ArrayBuffer,
): Promise<void> {
  const parts = relativePath.split('/');
  const fileName = parts.pop();
  if (!fileName) throw new Error(`Empty filename in path: ${relativePath}`);
  let current = rootHandle;
  for (const segment of parts) {
    current = await current.getDirectoryHandle(segment, { create: true });
  }
  const fileHandle = await current.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(data);
  await writable.close();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
